import React, { useState } from 'react';
import Card from '../Common/Card';
import Button from '../Common/Button';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { bpsToPercent, pctToBps } from '../../utils/format';
import { PLAN_INFO } from '../../config/contract';

const PlanManager = ({ plans, onChange }) => {
  const { contract } = useWeb3();
  const [busy, setBusy] = useState('');

  const [edits, setEdits] = useState(
    Array(5).fill(null).map(() => ({ roi: '', periods: '' }))
  );

  const updateField = (i, field, v) => {
    const cleaned = field === 'roi'
      ? v.replace(/[^0-9.]/g, '')
      : v.replace(/[^0-9]/g, '');
    setEdits((curr) => curr.map((e, idx) => idx === i ? { ...e, [field]: cleaned } : e));
  };

  const sendROI = async (i) => {
    if (!contract) return;
    setBusy(`roi-${i}`);
    let lid = null;
    try {
      lid = notify.loading('Updating ROI...');
      const tx = await contract.updatePlanDailyROI(i, pctToBps(edits[i].roi));
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success(`${PLAN_INFO[i].rank} ROI updated`);
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  const sendPeriods = async (i) => {
    if (!contract) return;
    setBusy(`p-${i}`);
    let lid = null;
    try {
      lid = notify.loading('Updating periods...');
      const tx = await contract.updatePlanPeriods(i, edits[i].periods);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success(`${PLAN_INFO[i].rank} periods updated`);
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="Plan Configuration" subtitle="Owner only · Adjust daily ROI and periods per tier">
      <div className="table-wrap" style={{ background: 'transparent', border: 'none', overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Tier</th>
              <th>Current ROI</th>
              <th>New ROI</th>
              <th>Current Periods</th>
              <th>New Periods</th>
            </tr>
          </thead>
          <tbody>
            {PLAN_INFO.map((p, i) => (
              <tr key={i}>
                <td>
                  <div style={{
                    fontFamily: 'Orbitron, sans-serif',
                    color: p.color,
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: '0.1em',
                    textShadow: `0 0 8px ${p.color}80`
                  }}>{p.rank}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.days}d default</div>
                </td>
                <td className="numeric">
                  {plans?.dailyROIs?.[i] !== undefined
                    ? `${bpsToPercent(plans.dailyROIs[i])}%`
                    : '-'}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ position: 'relative', width: 100 }}>
                      <input
                        className="form-input"
                        style={{ padding: '8px 28px 8px 10px', fontSize: 13 }}
                        placeholder="0.6"
                        value={edits[i].roi}
                        onChange={(e) => updateField(i, 'roi', e.target.value)}
                      />
                      <span style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 11,
                        color: 'var(--accent-cyan)',
                        fontFamily: 'Orbitron, sans-serif',
                        pointerEvents: 'none'
                      }}>%</span>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={busy === `roi-${i}`}
                      onClick={() => sendROI(i)}
                      disabled={!edits[i].roi}
                    >Set</Button>
                  </div>
                </td>
                <td className="numeric">{plans?.periods?.[i]?.toString() || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="form-input"
                      style={{ padding: '8px 10px', fontSize: 13, width: 90 }}
                      placeholder="days"
                      value={edits[i].periods}
                      onChange={(e) => updateField(i, 'periods', e.target.value)}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      loading={busy === `p-${i}`}
                      onClick={() => sendPeriods(i)}
                      disabled={!edits[i].periods}
                    >Set</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: 16,
        padding: '10px 14px',
        background: 'rgba(0, 212, 255, 0.05)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12,
        color: 'var(--text-secondary)'
      }}>
        ROI is per-period (one period = current ROI Timeframe). Periods = how many of those periods the plan lasts.
      </div>
    </Card>
  );
};

export default PlanManager;
